/**
 * ManualEntryButtons
 *
 * Compact inline buttons for manual entry options.
 * Displayed as a horizontal row of styled buttons.
 */

import { Mail, MapPin, PenLine, Printer } from 'lucide-react';
import React from 'react';

import { cn } from '@/lib/utils';
import type { CorrespondenceChannel } from '@/services/api/correspondenceService';

interface ManualEntryButtonsProps {
  selectedChannel: CorrespondenceChannel | null;
  isManualEntry: boolean;
  onSelect: (channel: CorrespondenceChannel) => void;
  isDisabled?: boolean;
}

const MANUAL_OPTIONS: Array<{
  channel: CorrespondenceChannel;
  icon: React.ElementType;
  label: string;
}> = [
  { channel: 'fax', icon: Printer, label: 'Fax' },
  { channel: 'email', icon: Mail, label: 'Email' },
  { channel: 'mailed_letter', icon: MapPin, label: 'Address' },
];

export const ManualEntryButtons: React.FC<ManualEntryButtonsProps> = ({
  selectedChannel,
  isManualEntry,
  onSelect,
  isDisabled = false,
}) => {
  return (
    <div className="flex items-center gap-1.5">
      {MANUAL_OPTIONS.map(({ channel, icon: Icon, label }) => {
        const isSelected = isManualEntry && selectedChannel === channel;

        return (
          <button
            key={channel}
            type="button"
            onClick={() => onSelect(channel)}
            disabled={isDisabled}
            className={cn(
              'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-medium transition-all border',
              isSelected
                ? 'bg-indigo-50/80 border-indigo-200 text-indigo-700'
                : isDisabled
                  ? 'bg-zinc-50/50 border-transparent text-zinc-300 cursor-not-allowed'
                  : 'bg-white border-zinc-100 text-zinc-500 hover:border-zinc-200 hover:text-zinc-700',
            )}
          >
            <Icon className="h-3 w-3" />
            <span>{label}</span>
            {isSelected && <PenLine className="h-2.5 w-2.5 text-indigo-500" />}
          </button>
        );
      })}
    </div>
  );
};
